import type { WorkData } from '../dto/EntityRepository';

/** 이 거리(px) 이상 움직이면 래소로 간주하고 행 click 은 무시 */
export const MARQUEE_GESTURE_MIN_PX = 5;

export type MarqueeDragState = {
  originX: number;
  originY: number;
  currentX: number;
  currentY: number;
};

export function marqueeDragToBox(
  drag: MarqueeDragState | null
): { left: number; top: number; width: number; height: number } | null {
  if (!drag) return null;
  const { originX: ox, originY: oy, currentX: cx, currentY: cy } = drag;
  const w = Math.abs(cx - ox);
  const h = Math.abs(cy - oy);
  if (w < MARQUEE_GESTURE_MIN_PX && h < MARQUEE_GESTURE_MIN_PX) return null;
  return {
    left: Math.min(ox, cx),
    top: Math.min(oy, cy),
    width: w,
    height: h,
  };
}

/** 스크롤 컨테이너 좌표계 기준 박스와 겹치는 행 id, DOM 순 */
export function collectRowIdsInMarquee(
  container: HTMLElement,
  box: { left: number; top: number; width: number; height: number },
  rowSelector: string,
  getIdFromElement: (el: HTMLElement) => string | undefined
): string[] {
  const marqRight = box.left + box.width;
  const marqBottom = box.top + box.height;
  const cRect = container.getBoundingClientRect();
  const out: string[] = [];
  container.querySelectorAll<HTMLElement>(rowSelector).forEach((marqueeEl) => {
    const r = marqueeEl.getBoundingClientRect();
    const left = r.left - cRect.left + container.scrollLeft;
    const top = r.top - cRect.top + container.scrollTop;
    const right = left + r.width;
    const bottom = top + r.height;
    if (box.left < right && marqRight > left && box.top < bottom && marqBottom > top) {
      const id = getIdFromElement(marqueeEl);
      if (id) out.push(id);
    }
  });
  return out;
}

/** 스크롤 컨테이너(WorkListComp) 좌표계 기준 박스와 겹치는 Work 행 id, DOM 순 */
export function collectWorkIdsInMarquee(
  container: HTMLElement,
  box: { left: number; top: number; width: number; height: number }
): string[] {
  return collectRowIdsInMarquee(
    container,
    box,
    '[data-work-id]',
    (el) => el.dataset.workId
  );
}

/** DOM 순 id 수집 후 `workList`에서 `WorkData`로 붙임 (`dataset.workId`는 문자열 → id 키 통일 필수) */
export function collectWorksInMarquee(
  container: HTMLElement,
  box: { left: number; top: number; width: number; height: number },
  workList: WorkData[]
): WorkData[] {
  const byId = new Map<string, WorkData>();
  for (const w of workList) {
    byId.set(String(w.id), w);
  }
  return collectWorkIdsInMarquee(container, box)
    .map((id) => byId.get(String(id)))
    .filter((w): w is WorkData => !!w);
}
